import type { ReactNode } from "react";
import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo, useReducer } from "react";

type SidebarLayout = "content" | "default";

type SidebarUiState = {
  open: boolean;
  layout: SidebarLayout;
};

type SidebarUiAction =
  | { type: "OPEN" }
  | { type: "CLOSE" }
  | { type: "TOGGLE" }
  | { type: "SET_LAYOUT"; payload: SidebarLayout };

type SidebarUiActions = {
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  setLayout: (layout: SidebarLayout) => void;
};

type SidebarUiContextValue = SidebarUiState & SidebarUiActions;

const defaultState: SidebarUiState = {
  open: false,
  layout: "default",
};

const noop = (): void => undefined;

const SidebarUiContext = createContext<SidebarUiContextValue>({
  ...defaultState,
  openSidebar: noop,
  closeSidebar: noop,
  toggleSidebar: noop,
  setLayout: noop,
});

function sidebarUiReducer(state: SidebarUiState, action: SidebarUiAction): SidebarUiState {
  switch (action.type) {
    case "OPEN":
      return { ...state, open: true };
    case "CLOSE":
      return { ...state, open: false };
    case "TOGGLE":
      return { ...state, open: !state.open };
    case "SET_LAYOUT":
      return { ...state, layout: action.payload };
  }
}

export const SidebarUiProvider: FC<{
  children: ReactNode;
  initialState?: Partial<SidebarUiState>;
}> = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(sidebarUiReducer, {
    ...defaultState,
    ...initialState,
  });

  const openSidebar = useCallback(() => {
    dispatch({ type: "OPEN" });
  }, []);

  const closeSidebar = useCallback(() => {
    dispatch({ type: "CLOSE" });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: "TOGGLE" });
  }, []);

  const setLayout = useCallback((layout: SidebarLayout) => {
    dispatch({ type: "SET_LAYOUT", payload: layout });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      setLayout,
    }),
    [state, openSidebar, closeSidebar, toggleSidebar, setLayout],
  );

  return <SidebarUiContext.Provider value={value}>{children}</SidebarUiContext.Provider>;
};

export function useSidebarUi(): SidebarUiContextValue {
  return useContext(SidebarUiContext);
}
